import { Box, Grid } from '@chakra-ui/react';
import {
  useFormFillingsSameSetting,
  useManagerFormFillingScoreSettings,
  usePeerFormFillingScoreSettings,
  useReviewCreationData,
  useSelfFormFillingScoreSettings,
} from 'reducers/reviewCreationSlice';

import { FormVisibilitySettings } from 'assets/assetsV1/iconComponents';

import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';

import { Accordion } from 'uiToolkitV1/Accordion';
import { AccordionButton, AccordionItem2, AccordionPanel2 } from 'uiToolkitV1/Accordion/Accordion';
import { Divider } from 'uiToolkitV1/Divider';
import {
  ExpandablePaper,
  ExpandablePaperBody,
  ExpandablePaperHeader,
} from 'uiToolkitV1/ExpandablePaper/ExpandablePaper';
import { TextHeader4 } from 'uiToolkitV1/Typography';

import {
  FormFilling,
  FormFillingScoreSettings,
  SELF_ANSWER_VISIBILITY_FOR_PEERS_VALUES_LABELS,
  useGetFormFillings,
} from '../../Hooks/dataHooks/formFillingsDataHooks';
import { Detail, RenderDetails } from './RenderDetails';

const SelfScoreSetting: React.FC<{ setting: FormFilling }> = ({ setting }) => {
  const selfFormFillingScoreSettings: FormFillingScoreSettings['selfFormFillingScoreSettings'] =
    useSelfFormFillingScoreSettings({ feedbackFormId: setting.feedbackFormId });

  const visibleScores = [];
  if (selfFormFillingScoreSettings.isShowGoalRating) {
    visibleScores.push('Overall Goals Score');
  }
  if (selfFormFillingScoreSettings.isShowCompetencyRating) {
    visibleScores.push('Overall Competency Score');
  }
  if (selfFormFillingScoreSettings.isShowOverallRating) {
    visibleScores.push('Overall Score');
  }

  const details: Detail[] = [
    {
      key: 'Scores self can see before submitting review forms',
      value: visibleScores.join(', '),
    },
  ];

  return <RenderDetails details={details} />;
};

const ManagerScoreSetting: React.FC<{ setting: FormFilling }> = ({ setting }) => {
  const managerFormFillingScoreSettings: FormFillingScoreSettings['managerFormFillingScoreSettings'] =
    useManagerFormFillingScoreSettings({ feedbackFormId: setting.feedbackFormId });

  const visibleScores = [
    ...(managerFormFillingScoreSettings.isShowGoalRating ? ['Overall Goals Score'] : []),
    'Overall Competency Score',
    'Overall Score',
  ];

  const hasDLM = setting.enabledProviderTypes.includes(REVIEW_PROVIDER_TYPES.DLM);

  const details: Detail[] = [
    {
      key: hasDLM
        ? 'Scores manager and Dotted Line Managers can see before submitting review forms'
        : 'Scores manager can see before submitting review forms',
      value: visibleScores.join(', '),
    },
  ];

  return <RenderDetails details={details} />;
};

const PeerCanSeeSelfAnswers: React.FC<{ setting: FormFilling }> = ({ setting }) => {
  const hasPeer = setting.enabledProviderTypes.includes(REVIEW_PROVIDER_TYPES.PEER);

  const peerSettings: FormFillingScoreSettings['peerFormFillingScoreSettings'] = usePeerFormFillingScoreSettings({
    feedbackFormId: setting.feedbackFormId,
  });

  if (!hasPeer) return null;

  const details: Detail[] = [
    {
      key: 'Can peer see self answers while filling out their forms',
      value:
        peerSettings.selfAnswerVisibilityForPeers?.length > 0
          ? peerSettings.selfAnswerVisibilityForPeers
              .map((section) => SELF_ANSWER_VISIBILITY_FOR_PEERS_VALUES_LABELS[section])
              .join(', ')
          : 'No section visible',
    },
  ];

  return <RenderDetails details={details} />;
};

const SettingPanel: React.FC<{ settings: FormFilling[]; setting: FormFilling }> = ({ settings, setting }) => {
  const settingSameAsFormId = useFormFillingsSameSetting({
    feedbackFormId: setting.feedbackFormId,
  });

  // disabling this rule for now as we might need this in future
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const settingsSameAsCohort = settings.find((s) => s.feedbackFormId === settingSameAsFormId);

  return (
    <Accordion defaultIndex={0} allowMultiple allowToggle boxShadow="none !important" borderRadius="8px" mt={1}>
      <AccordionItem2 sxProps={{ borderBottom: 'none !important', p: '12px' }}>
        <AccordionButton p="20px 24px !important">
          <TextHeader4 bold>{setting?.feedbackFormName}</TextHeader4>
          {/* {settingsSameAsCohort && (
            <Flex ml="auto">
              <TextBodyMedium mr={1}>Using same settings as</TextBodyMedium>
              <TextBodyMedium bold>{settingsSameAsCohort?.cohort?.name}</TextBodyMedium>
              <TooltipInfoV1 title="tooltip" ml={2} />
            </Flex>
          )} */}
        </AccordionButton>
        <AccordionPanel2 p={0}>
          <Box bg="white">
            <Divider borderColor="neutral.CFD7DF" mx={4} />
          </Box>
          <Grid templateColumns="1fr 2fr" rowGap={8} columnGap={8} p="24px 24px 28px" bgColor="white">
            <SelfScoreSetting setting={setting} />
            <ManagerScoreSetting setting={setting} />
            <PeerCanSeeSelfAnswers setting={setting} />
          </Grid>
        </AccordionPanel2>
      </AccordionItem2>
    </Accordion>
  );
};

export const FormFillingExperience = () => {
  const { reviewCycleId } = useReviewCreationData();
  const { data: { formFillingSettings } = {}, isLoading } = useGetFormFillings({ reviewCycleId });

  if (formFillingSettings?.length === 1) {
    const setting = formFillingSettings[0];

    return (
      <ExpandablePaper showLoader={isLoading}>
        <ExpandablePaperHeader
          title="Form Filling Experience"
          leftIconComponent={<FormVisibilitySettings boxSize="24px" color="icon.g2" />}
        />
        <ExpandablePaperBody>
          <Grid templateColumns="1fr 2fr" rowGap={8} columnGap={8} p="16px 8px 12px">
            <SelfScoreSetting setting={setting} />
            <ManagerScoreSetting setting={setting} />
            <PeerCanSeeSelfAnswers setting={setting} />
          </Grid>
        </ExpandablePaperBody>
      </ExpandablePaper>
    );
  }

  return (
    <ExpandablePaper showLoader={isLoading}>
      <ExpandablePaperHeader
        title="Form Filling Experience"
        leftIconComponent={<FormVisibilitySettings boxSize="24px" color="icon.g2" />}
        borderBottom="none"
        marginBottom={0}
      />
      <ExpandablePaperBody p="0 4px 4px 4px" bgColor="neutral.EBEFF3" borderRadius="8px">
        {formFillingSettings?.map((setting) => <SettingPanel settings={formFillingSettings} setting={setting} />)}
      </ExpandablePaperBody>
    </ExpandablePaper>
  );
};
